
import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, AfterViewInit, ElementRef, ViewChild, ChangeDetectorRef, InjectionToken, inject } from '@angular/core';

export type LoadingModalIcon = "box" | "ring";

export const LOADING_MODAL_ICON = new InjectionToken<LoadingModalIcon>("LOADING_MODAL_ICON", {
  providedIn: "root",
  factory: () => "box",
});

export const LOADING_MODAL_SHOW_PROGRESS_BAR = new InjectionToken<boolean>("LOADING_MODAL_SHOW_PROGRESS_BAR", {
  providedIn: "root",
  factory: () => true,
});

@Component({
  selector: "app-loading-modal",
  standalone: true,
  templateUrl: "./loading-modal.component.html",
  styleUrls: ["./loading-modal.component.scss"],
  imports: [CommonModule],
})
export class LoadingModalComponent implements OnInit, AfterViewInit {
  @Input() content: string = "";
  @Input() showLoadingBar = false;
  // Shared across every cb-ui page and fedex-ui - a plain input rather than
  // injecting an app-level "is this the redesign" service directly, since
  // this is a cross-app library component with no concept of cb-ui's
  // redesign variant (and fedex-ui has no such concept at all).
  @Input() isRedesign = false;

  readonly icon = inject(LOADING_MODAL_ICON);
  readonly showProgressBar = inject(LOADING_MODAL_SHOW_PROGRESS_BAR);

  readonly defaultBodyText = "It may take up to 90 seconds to generate your project. Thank you for your patience!";
  hasProjectedBodyText = false;

  @ViewChild("bodyText") private bodyTextRef?: ElementRef<HTMLElement>;

  public loadingProgress = 0;

  constructor(private cdr: ChangeDetectorRef) {}

  async ngOnInit() {
    if (this.showLoadingBar && this.showProgressBar) {
      await this.increaseLoadingProgress();
    }
  }

  ngAfterViewInit(): void {
    // Detect projected content synchronously here (rather than reading it lazily in the
    // template) and force a change-detection pass so the same value is seen by Angular's
    // dev-mode "no changes" check — otherwise mutating a template-bound flag from
    // ngAfterViewInit throws ExpressionChangedAfterItHasBeenCheckedError.
    this.hasProjectedBodyText = !!this.bodyTextRef?.nativeElement.textContent?.trim();
    this.cdr.detectChanges();
  }

  increaseLoadingProgress() {
    return new Promise<void>((resolve) => {
      const increment = 50;
      if (this.loadingProgress < 50) {
        this.loadingProgress += 0.5;
        setTimeout(() => resolve(this.increaseLoadingProgress()), increment);
      } else if (this.loadingProgress < 98) {
        this.loadingProgress += 0.25;
        setTimeout(() => resolve(this.increaseLoadingProgress()), increment);
      } else {
        resolve();
      }
    });
  }
}
